import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import classNames from 'classnames';

import {
  FlexibleWidthXYPlot,
  XAxis,
  YAxis,
  HorizontalGridLines,
  VerticalBarSeries,
  VerticalRectSeries,
  DiscreteColorLegend,
  Hint,
} from 'react-vis';

import Spinner from '../../common/Spinner';

import 'react-vis/dist/style.css';

class Stats extends Component {
  static propTypes = {
    stats: PropTypes.object,
    base: PropTypes.oneOf(['series', 'school', 'group']).isRequired,
    compareSeries: PropTypes.bool,
    compareSchool: PropTypes.bool,
    showParticipation: PropTypes.bool,
  };

  static defaultProps = {
    stats: null,
    compareSeries: false,
    compareSchool: false,
    showParticipation: false,
  };

  state = {
    pointsHint: null,
    answerSolveHint: null,
    pointsDistribution: null,
  };

  static getDerivedStateFromProps(props, state) {
    if (state.pointsDistribution) return null;
    return { pointsDistribution: props.base };
  }

  renderParticipationStats = () => {
    const { stats, base, compareSeries, compareSchool } = this.props;
    return (
      <>
        <h4 className="mt-0 mb-3">
          <FormattedMessage
            id="stats-participation-header"
            defaultMessage="Osallistujamäärä"
          />
        </h4>

        <div className="row">
          {(base === 'series' || compareSeries) && (
            <div className="col-3">
              <div className="font-weight-bold">
                <FormattedMessage
                  id="stats-participation-series"
                  defaultMessage="Valtakunnallisesti"
                />
              </div>

              {stats.series.participants}
            </div>
          )}

          {(base === 'school' || compareSchool) && (
            <div className="col-3">
              <div className="font-weight-bold">
                <FormattedMessage
                  id="stats-participation-school"
                  defaultMessage="Omasta koulustasi"
                />
              </div>

              {stats.school.participants}
            </div>
          )}

          {base === 'group' && (
            <div className="col-3">
              <div className="font-weight-bold">
                <FormattedMessage
                  id="stats-participation-group"
                  defaultMessage="Omasta ryhmästäsi"
                />
              </div>

              {stats.group.participants}
            </div>
          )}
        </div>
      </>
    );
  };

  renderPointsStats = () => {
    const { stats, base, compareSeries, compareSchool } = this.props;

    const renderValue = (key, manipulate = v => v) => (
      <div>
        <span className="mr-3">
          <FormattedMessage
            id="stats-points-value"
            defaultMessage="{points} pistettä"
            values={{
              points:
                stats[base][key] === null ? '-' : manipulate(stats[base][key]),
            }}
          />
        </span>

        {compareSeries && (
          <span className="mr-3">
            <i
              className="fas fa-globe text-primary mr-2"
              style={{ marginBottom: '3px' }}
            />

            {stats.series[key] === null ? '-' : manipulate(stats.series[key])}
          </span>
        )}

        {compareSchool && (
          <span className="mr-3">
            <i
              className="fas fa-school text-primary mr-2"
              style={{ marginBottom: '3px' }}
            />

            {stats.school[key] === null ? '-' : manipulate(stats.school[key])}
          </span>
        )}
      </div>
    );

    return (
      <>
        <h4 className="mb-3">
          <FormattedMessage
            id="stats-points-header"
            defaultMessage="Pistetilastot"
          />
        </h4>

        <div className="row">
          <div className="col-3">
            <div className="font-weight-bold">
              <FormattedMessage
                id="stats-points-average-title"
                defaultMessage="Keskiarvo"
              />
            </div>

            {renderValue('average_points', v => v.toFixed(2))}
          </div>

          <div className="col-3">
            <div className="font-weight-bold">
              <FormattedMessage
                id="stats-points-best-title"
                defaultMessage="Paras tulos"
              />
            </div>

            {renderValue('best_points')}
          </div>

          <div className="col-3">
            <div className="font-weight-bold">
              <FormattedMessage
                id="stats-points-max-title"
                defaultMessage="Paras mahdollinen tulos"
              />
            </div>

            <div>
              <FormattedMessage
                id="stats-points-max"
                defaultMessage="{points} pistettä"
                values={{
                  points: stats[base].max_points,
                }}
              />
            </div>
          </div>
        </div>

        <div className="row mt-3">
          <div className="col-3">
            <div className="font-weight-bold">
              <FormattedMessage
                id="stats-points-top-5-percentile-title"
                defaultMessage="Paras 5%"
              />
            </div>

            {renderValue('top_5_percentile_points')}
          </div>

          <div className="col-3">
            <div className="font-weight-bold">
              <FormattedMessage
                id="stats-points-upper-quartile-title"
                defaultMessage="Yläkvartiili"
              />
            </div>

            {renderValue('upper_quartile_points')}
          </div>

          <div className="col-3">
            <div className="font-weight-bold">
              <FormattedMessage
                id="stats-points-median-title"
                defaultMessage="Mediaani"
              />
            </div>

            {renderValue('median_points')}
          </div>

          <div className="col-3">
            <div className="font-weight-bold">
              <FormattedMessage
                id="stats-points-lower-quartile-title"
                defaultMessage="Alakvartiili"
              />
            </div>

            {renderValue('lower_quartile_points')}
          </div>
        </div>
      </>
    );
  };

  renderStreakStats = () => {
    const { stats, base, compareSeries, compareSchool } = this.props;

    const renderValue = (key, manipulate = v => v) => (
      <div>
        <span className="mr-4">
          {stats[base][key] === null ? '-' : manipulate(stats[base][key])}
        </span>

        {compareSeries && (
          <span className="mr-3">
            <i
              className="fas fa-globe text-primary mr-2"
              style={{ marginBottom: '3px' }}
            />

            {stats.series[key] === null ? '-' : manipulate(stats.series[key])}
          </span>
        )}

        {compareSchool && (
          <span className="mr-3">
            <i
              className="fas fa-school text-primary mr-2"
              style={{ marginBottom: '3px' }}
            />

            {stats.school[key] === null ? '-' : manipulate(stats.school[key])}
          </span>
        )}
      </div>
    );

    return (
      <>
        <h4 className="mb-3">
          <FormattedMessage
            id="stats-streaks-header"
            defaultMessage="Loikkatilastot"
          />
        </h4>

        <div className="row">
          <div className="col-3">
            <div className="font-weight-bold">
              <FormattedMessage
                id="stats-streaks-average-title"
                defaultMessage="Keskiarvo"
              />
            </div>

            {renderValue('average_streak', v => v.toFixed(2))}
          </div>

          <div className="col-3">
            <div className="font-weight-bold">
              <FormattedMessage
                id="stats-streaks-best-title"
                defaultMessage="Paras tulos"
              />
            </div>

            {renderValue('best_streak')}
          </div>

          <div className="col-3">
            <div className="font-weight-bold">
              <FormattedMessage
                id="stats-streaks-max-title"
                defaultMessage="Paras mahdollinen tulos"
              />
            </div>

            <div>
              <FormattedMessage
                id="stats-streaks-max"
                defaultMessage="{points}"
                values={{
                  points: stats[base].max_streak,
                }}
              />
            </div>
          </div>
        </div>

        <div className="row mt-3">
          <div className="col-3">
            <div className="font-weight-bold">
              <FormattedMessage
                id="stats-streaks-top-5-percentile-title"
                defaultMessage="Paras 5%"
              />
            </div>

            {renderValue('top_5_percentile_streak')}
          </div>

          <div className="col-3">
            <div className="font-weight-bold">
              <FormattedMessage
                id="stats-streaks-upper-quartile-title"
                defaultMessage="Yläkvartiili"
              />
            </div>

            {renderValue('upper_quartile_streak')}
          </div>

          <div className="col-3">
            <div className="font-weight-bold">
              <FormattedMessage
                id="stats-streaks-median-title"
                defaultMessage="Mediaani"
              />
            </div>

            {renderValue('median_streak')}
          </div>

          <div className="col-3">
            <div className="font-weight-bold">
              <FormattedMessage
                id="stats-streaks-lower-quartile-title"
                defaultMessage="Alakvartiili"
              />
            </div>

            {renderValue('lower_quartile_streak')}
          </div>
        </div>
      </>
    );
  };

  renderPointsDistribution = () => {
    const { stats, base, compareSeries, compareSchool } = this.props;
    const { pointsHint, pointsDistribution } = this.state;

    return (
      <>
        <h4 className="mb-3">
          <FormattedMessage
            id="stats-histogram"
            defaultMessage="Pistejakauma"
          />
        </h4>

        {(compareSeries || compareSchool) && (
          <ul className="nav nav-tabs justify-content-center">
            <li className="nav-item">
              <button
                type="button"
                onClick={() => this.setState({ pointsDistribution: base })}
                className={classNames('nav-link', {
                  active: !pointsDistribution || pointsDistribution === base,
                })}
              >
                {base === 'group' && (
                  <FormattedMessage id="stats-group" defaultMessage="Ryhmä" />
                )}

                {base === 'school' && (
                  <FormattedMessage id="stats-school" defaultMessage="Koulu" />
                )}

                {base === 'series' && (
                  <FormattedMessage
                    id="stats-series"
                    defaultMessage="Valtakunta"
                  />
                )}
              </button>
            </li>

            {compareSchool && (
              <li className="nav-item">
                <button
                  type="button"
                  onClick={() =>
                    this.setState({ pointsDistribution: 'school' })
                  }
                  className={classNames('nav-link', {
                    active: pointsDistribution === 'school',
                  })}
                >
                  <FormattedMessage id="stats-school" defaultMessage="Koulu" />
                </button>
              </li>
            )}

            {compareSeries && (
              <li className="nav-item">
                <button
                  type="button"
                  onClick={() =>
                    this.setState({ pointsDistribution: 'series' })
                  }
                  className={classNames('nav-link', {
                    active: pointsDistribution === 'series',
                  })}
                >
                  <FormattedMessage
                    id="stats-series"
                    defaultMessage="Valtakunta"
                  />
                </button>
              </li>
            )}
          </ul>
        )}

        <FlexibleWidthXYPlot height={400} margin={{ bottom: 70 }} stackBy="y">
          <HorizontalGridLines />
          <VerticalRectSeries
            onValueMouseOut={() => this.setState({ pointsHint: null })}
            onValueMouseOver={d => this.setState({ pointsHint: d })}
            color="#F96332"
            stroke="#FFFFFF"
            data={stats[pointsDistribution].points_distribution.map(
              (h, index) => ({
                x: index + 1,
                x0: index,
                y: h.frequency,
                y0: 0,
              })
            )}
          />
          <XAxis
            tickFormat={(value, index) => {
              const h = stats[pointsDistribution].points_distribution[index];
              if (!h) return '';
              if (h.range_min === -1 && h.range_max === -1) return '';

              return `${!index ? '[' : '('}${h.range_min}, ${h.range_max}]`;
            }}
            tickLabelAngle={-30}
            tickPadding={15}
            tickValues={stats[pointsDistribution].points_distribution.map(
              (_, index) => index + 0.5
            )}
          />
          <YAxis tickFormat={v => (Number.isInteger(v) ? v : '')} />
          {pointsHint && (
            <Hint
              value={pointsHint}
              style={{ marginBottom: '10px' }}
              format={d => [
                {
                  title: (
                    <FormattedMessage
                      id="stats-frequency"
                      defaultMessage="lukumäärä"
                    />
                  ),
                  value: d.y,
                },
              ]}
              align={{ vertical: 'top', horizontal: 'right' }}
            />
          )}
        </FlexibleWidthXYPlot>
      </>
    );
  };

  renderAnswerDistribution = () => {
    const { stats, base, compareSeries, compareSchool } = this.props;

    const questions = [];
    stats[base].choice_answer_percentages.forEach(cap => {
      if (questions.find(q => q.id === cap.question_id)) return;

      questions.push({
        id: cap.question_id,
        number: cap.question_number,
        choice_ids: stats[base].choice_answer_percentages
          .filter(c => c.question_id === cap.question_id)
          .map(c => c.choice_id),
      });
    });

    const maxChoices = Math.max(...questions.map(q => q.choice_ids.length));

    return (
      <>
        <h4 className="mb-3">
          <FormattedMessage
            id="stats-answer-distribution"
            defaultMessage="Vastausten jakauma"
          />
        </h4>

        <table className="table table-hover">
          <thead>
            <tr>
              <th aria-label="question" />

              {[...Array(maxChoices)]
                .map((_, index) => index)
                .map(choiceIndex => (
                  <th key={choiceIndex} className="text-center">
                    {String.fromCharCode(97 + choiceIndex)}
                  </th>
                ))}
            </tr>
          </thead>
          <tbody>
            {questions.map(question => (
              <tr key={question.id}>
                <td className="align-middle">Kysymys #{question.number}</td>

                {[...Array(maxChoices)]
                  .map((_, index) => index)
                  .map(choiceIndex => {
                    const choiceId = question.choice_ids[choiceIndex];

                    const cap = stats[base].choice_answer_percentages.find(
                      c =>
                        c.question_id === question.id &&
                        c.choice_id === choiceId
                    );

                    const seriesCap =
                      compareSeries &&
                      stats.series.choice_answer_percentages.find(
                        c =>
                          c.question_id === question.id &&
                          c.choice_id === choiceId
                      );

                    const schoolCap =
                      compareSchool &&
                      stats.school.choice_answer_percentages.find(
                        c =>
                          c.question_id === question.id &&
                          c.choice_id === choiceId
                      );

                    return (
                      <td
                        key={choiceIndex}
                        className={classNames(
                          'text-center',
                          'align-middle',
                          'text-nowrap',
                          {
                            'font-weight-bold': cap && cap.is_correct,
                          }
                        )}
                      >
                        {cap && (
                          <div>{Math.round(cap.answer_percentage * 100)}%</div>
                        )}

                        {seriesCap && (
                          <span className="small mr-3">
                            <i className="fa fa-globe text-primary mr-1" />
                            {Math.round(seriesCap.answer_percentage * 100)}%
                          </span>
                        )}

                        {schoolCap && (
                          <span className="small">
                            <i className="fa fa-school text-primary mr-1" />
                            {Math.round(schoolCap.answer_percentage * 100)}%
                          </span>
                        )}
                      </td>
                    );
                  })}
              </tr>
            ))}
          </tbody>
        </table>
      </>
    );
  };

  renderSolvePercentages = () => {
    const { stats, base, compareSeries, compareSchool } = this.props;
    const { answerSolveHint } = this.state;

    const solveSeriesColor = '#D4EDFF';
    const solveSchoolColor = '#80CAFF';
    const solveBaseColor = '#2CA8FF';
    const answerSeriesColor = '#FDDFD6';
    const answerSchoolColor = '#FBA184';
    const answerBaseColor = '#F96332';

    const legend = [
      {
        title: (
          <FormattedMessage
            id="stats-solve-percentages-solve-legend"
            defaultMessage="Ratkaisuprosentti"
          />
        ),
        color: solveBaseColor,
      },
    ];

    if (compareSchool) {
      legend.push({
        title: (
          <span>
            <i className="fas fa-school text-primary mr-2" />
            <FormattedMessage
              id="stats-solve-percentages-solve-legend"
              defaultMessage="Ratkaisuprosentti"
            />
          </span>
        ),
        color: solveSchoolColor,
      });
    }

    if (compareSeries) {
      legend.push({
        title: (
          <span>
            <i className="fas fa-globe text-primary mr-2" />
            <FormattedMessage
              id="stats-solve-percentages-solve-legend"
              defaultMessage="Ratkaisuprosentti"
            />
          </span>
        ),
        color: solveSeriesColor,
      });
    }

    legend.push({
      title: (
        <FormattedMessage
          id="stats-solve-percentages-answer-legend"
          defaultMessage="Vastausprosentti"
        />
      ),
      color: answerBaseColor,
    });

    if (compareSchool) {
      legend.push({
        title: (
          <span>
            <i className="fas fa-school text-primary mr-2" />
            <FormattedMessage
              id="stats-solve-percentages-answer-legend"
              defaultMessage="Vastausprosentti"
            />
          </span>
        ),
        color: answerSchoolColor,
      });
    }

    if (compareSeries) {
      legend.push({
        title: (
          <span>
            <i className="fas fa-globe text-primary mr-2" />
            <FormattedMessage
              id="stats-solve-percentages-answer-legend"
              defaultMessage="Vastausprosentti"
            />
          </span>
        ),
        color: answerSeriesColor,
      });
    }

    return (
      <>
        <h4 className="mb-3">
          <FormattedMessage
            id="stats-solve-percentages"
            defaultMessage="Kysymysten ratkaisuprosentit"
          />
        </h4>

        <FlexibleWidthXYPlot
          xType="ordinal"
          height={400}
          yDomain={[0, 100]}
          stackBy="y"
        >
          <HorizontalGridLines />

          {compareSeries && (
            <VerticalBarSeries
              cluster="solve"
              onValueMouseOut={() => this.setState({ answerSolveHint: null })}
              onValueMouseOver={d => this.setState({ answerSolveHint: d })}
              barWidth={0.5}
              color={solveSeriesColor}
              data={stats[base].answer_solve_percentages.map((asp, index) => {
                const baseSP = asp.solve_percentage * 100;
                const seriesSP =
                  stats.series.answer_solve_percentages[index]
                    .solve_percentage * 100;
                const schoolSP =
                  stats.school &&
                  stats.school.answer_solve_percentages[index]
                    .solve_percentage * 100;

                return {
                  x: asp.question_number,
                  // if series bar is smallest, show it here
                  y:
                    seriesSP < baseSP && (!compareSchool || seriesSP < schoolSP)
                      ? seriesSP
                      : 0,
                  asp,
                  index,
                };
              })}
            />
          )}

          {compareSchool && (
            <VerticalBarSeries
              cluster="solve"
              onValueMouseOut={() => this.setState({ answerSolveHint: null })}
              onValueMouseOver={d => this.setState({ answerSolveHint: d })}
              barWidth={0.5}
              color={solveSchoolColor}
              data={stats[base].answer_solve_percentages.map((asp, index) => {
                const baseSP = asp.solve_percentage * 100;
                const seriesSP =
                  stats.series &&
                  stats.series.answer_solve_percentages[index]
                    .solve_percentage * 100;
                const schoolSP =
                  stats.school.answer_solve_percentages[index]
                    .solve_percentage * 100;

                return {
                  x: asp.question_number,
                  // if school bar is smallest, show it here
                  y:
                    schoolSP < baseSP && (!compareSeries || schoolSP < seriesSP)
                      ? schoolSP
                      : 0,
                  asp,
                  index,
                };
              })}
            />
          )}

          <VerticalBarSeries
            cluster="solve"
            onValueMouseOut={() => this.setState({ answerSolveHint: null })}
            onValueMouseOver={d => this.setState({ answerSolveHint: d })}
            barWidth={0.5}
            color={solveBaseColor}
            data={stats[base].answer_solve_percentages.map((asp, index) => {
              const baseSP = asp.solve_percentage * 100;
              const seriesSP =
                stats.series &&
                stats.series.answer_solve_percentages[index].solve_percentage *
                  100;
              const schoolSP =
                stats.school &&
                stats.school.answer_solve_percentages[index].solve_percentage *
                  100;

              return {
                x: asp.question_number,
                // if base bar is smallest, show it here
                y:
                  (!compareSeries || baseSP < seriesSP) &&
                  (!compareSchool || baseSP < schoolSP)
                    ? baseSP
                    : 0,
                asp,
                index,
              };
            })}
          />

          {compareSeries && compareSchool && (
            <VerticalBarSeries
              cluster="solve"
              onValueMouseOut={() => this.setState({ answerSolveHint: null })}
              onValueMouseOver={d => this.setState({ answerSolveHint: d })}
              barWidth={0.5}
              color={solveSeriesColor}
              data={stats[base].answer_solve_percentages.map((asp, index) => {
                const baseSP = asp.solve_percentage * 100;
                const seriesSP =
                  stats.series.answer_solve_percentages[index]
                    .solve_percentage * 100;
                const schoolSP =
                  stats.school.answer_solve_percentages[index]
                    .solve_percentage * 100;

                let y = 0;
                if (seriesSP < baseSP && seriesSP > schoolSP) {
                  y = seriesSP - schoolSP;
                } else if (seriesSP < schoolSP && seriesSP > baseSP) {
                  y = seriesSP - baseSP;
                }

                return {
                  x: asp.question_number,
                  // if series bar is second smallest, show it here
                  y,
                  asp,
                  index,
                };
              })}
            />
          )}

          {compareSeries && compareSchool && (
            <VerticalBarSeries
              cluster="solve"
              onValueMouseOut={() => this.setState({ answerSolveHint: null })}
              onValueMouseOver={d => this.setState({ answerSolveHint: d })}
              barWidth={0.5}
              color={solveSchoolColor}
              data={stats[base].answer_solve_percentages.map((asp, index) => {
                const baseSP = asp.solve_percentage * 100;
                const seriesSP =
                  stats.series.answer_solve_percentages[index]
                    .solve_percentage * 100;
                const schoolSP =
                  stats.school.answer_solve_percentages[index]
                    .solve_percentage * 100;

                let y = 0;
                if (schoolSP < baseSP && schoolSP > seriesSP) {
                  y = schoolSP - seriesSP;
                } else if (schoolSP < seriesSP && schoolSP > baseSP) {
                  y = schoolSP - baseSP;
                }

                return {
                  x: asp.question_number,
                  // if school bar is second smallest, show it here
                  y,
                  asp,
                  index,
                };
              })}
            />
          )}

          {compareSeries && compareSchool && (
            <VerticalBarSeries
              cluster="solve"
              onValueMouseOut={() => this.setState({ answerSolveHint: null })}
              onValueMouseOver={d => this.setState({ answerSolveHint: d })}
              barWidth={0.5}
              color={solveBaseColor}
              data={stats[base].answer_solve_percentages.map((asp, index) => {
                const baseSP = asp.solve_percentage * 100;
                const seriesSP =
                  stats.series.answer_solve_percentages[index]
                    .solve_percentage * 100;
                const schoolSP =
                  stats.school.answer_solve_percentages[index]
                    .solve_percentage * 100;

                let y = 0;
                if (baseSP < seriesSP && baseSP > schoolSP) {
                  y = baseSP - schoolSP;
                } else if (baseSP < schoolSP && baseSP > seriesSP) {
                  y = baseSP - seriesSP;
                }

                return {
                  x: asp.question_number,
                  // if base bar is second smallest, show it here
                  y,
                  asp,
                  index,
                };
              })}
            />
          )}

          {compareSeries && (
            <VerticalBarSeries
              cluster="solve"
              onValueMouseOut={() => this.setState({ answerSolveHint: null })}
              onValueMouseOver={d => this.setState({ answerSolveHint: d })}
              barWidth={0.5}
              color={solveSeriesColor}
              data={stats[base].answer_solve_percentages.map((asp, index) => {
                const baseSP = asp.solve_percentage * 100;
                const seriesSP =
                  stats.series.answer_solve_percentages[index]
                    .solve_percentage * 100;
                const schoolSP =
                  stats.school &&
                  stats.school.answer_solve_percentages[index]
                    .solve_percentage * 100;

                let y = 0;
                if (
                  seriesSP > baseSP &&
                  (!compareSchool || seriesSP > schoolSP)
                ) {
                  if (schoolSP && schoolSP > baseSP) y = seriesSP - schoolSP;
                  else y = seriesSP - baseSP;
                }

                return {
                  x: asp.question_number,
                  // if series bar is biggest, show it here
                  y,
                  asp,
                  index,
                };
              })}
            />
          )}

          {compareSchool && (
            <VerticalBarSeries
              cluster="solve"
              onValueMouseOut={() => this.setState({ answerSolveHint: null })}
              onValueMouseOver={d => this.setState({ answerSolveHint: d })}
              barWidth={0.5}
              color={solveSchoolColor}
              data={stats[base].answer_solve_percentages.map((asp, index) => {
                const baseSP = asp.solve_percentage * 100;
                const seriesSP =
                  stats.series &&
                  stats.series.answer_solve_percentages[index]
                    .solve_percentage * 100;
                const schoolSP =
                  stats.school.answer_solve_percentages[index]
                    .solve_percentage * 100;

                let y = 0;
                if (
                  schoolSP > baseSP &&
                  (!compareSeries || schoolSP > seriesSP)
                ) {
                  if (seriesSP && seriesSP > baseSP) y = schoolSP - seriesSP;
                  else y = schoolSP - baseSP;
                }

                return {
                  x: asp.question_number,
                  // if school bar is biggest, show it here
                  y,
                  asp,
                  index,
                };
              })}
            />
          )}

          <VerticalBarSeries
            cluster="solve"
            onValueMouseOut={() => this.setState({ answerSolveHint: null })}
            onValueMouseOver={d => this.setState({ answerSolveHint: d })}
            barWidth={0.5}
            color={solveBaseColor}
            data={stats[base].answer_solve_percentages.map((asp, index) => {
              const baseSP = asp.solve_percentage * 100;
              const seriesSP =
                stats.series &&
                stats.series.answer_solve_percentages[index].solve_percentage *
                  100;
              const schoolSP =
                stats.school &&
                stats.school.answer_solve_percentages[index].solve_percentage *
                  100;

              let y = 0;
              if (
                compareSeries &&
                compareSchool &&
                baseSP > seriesSP &&
                baseSP > schoolSP
              ) {
                if (seriesSP > schoolSP) y = baseSP - seriesSP;
                else y = baseSP - schoolSP;
              } else if (compareSeries && !compareSchool && baseSP > seriesSP) {
                y = baseSP - seriesSP;
              } else if (!compareSeries && compareSchool && baseSP > schoolSP) {
                y = baseSP - schoolSP;
              }

              return {
                x: asp.question_number,
                // if base bar is biggest, show it here
                y,
                asp,
                index,
              };
            })}
          />

          {compareSeries && (
            <VerticalBarSeries
              cluster="answer"
              onValueMouseOut={() => this.setState({ answerSolveHint: null })}
              onValueMouseOver={d => this.setState({ answerSolveHint: d })}
              barWidth={0.5}
              color={answerSeriesColor}
              data={stats[base].answer_solve_percentages.map((asp, index) => {
                const baseSP = asp.answer_percentage * 100;
                const seriesSP =
                  stats.series.answer_solve_percentages[index]
                    .answer_percentage * 100;
                const schoolSP =
                  stats.school &&
                  stats.school.answer_solve_percentages[index]
                    .answer_percentage * 100;

                return {
                  x: asp.question_number,
                  // if series bar is smallest, show it here
                  y:
                    seriesSP < baseSP && (!compareSchool || seriesSP < schoolSP)
                      ? seriesSP
                      : 0,
                  asp,
                  index,
                };
              })}
            />
          )}

          {compareSchool && (
            <VerticalBarSeries
              cluster="answer"
              onValueMouseOut={() => this.setState({ answerSolveHint: null })}
              onValueMouseOver={d => this.setState({ answerSolveHint: d })}
              barWidth={0.5}
              color={answerSchoolColor}
              data={stats[base].answer_solve_percentages.map((asp, index) => {
                const baseSP = asp.answer_percentage * 100;
                const seriesSP =
                  stats.series &&
                  stats.series.answer_solve_percentages[index]
                    .answer_percentage * 100;
                const schoolSP =
                  stats.school.answer_solve_percentages[index]
                    .answer_percentage * 100;

                return {
                  x: asp.question_number,
                  // if school bar is smallest, show it here
                  y:
                    schoolSP < baseSP && (!compareSeries || schoolSP < seriesSP)
                      ? schoolSP
                      : 0,
                  asp,
                  index,
                };
              })}
            />
          )}

          <VerticalBarSeries
            cluster="answer"
            onValueMouseOut={() => this.setState({ answerSolveHint: null })}
            onValueMouseOver={d => this.setState({ answerSolveHint: d })}
            barWidth={0.5}
            color={answerBaseColor}
            data={stats[base].answer_solve_percentages.map((asp, index) => {
              const baseSP = asp.answer_percentage * 100;
              const seriesSP =
                stats.series &&
                stats.series.answer_solve_percentages[index].answer_percentage *
                  100;
              const schoolSP =
                stats.school &&
                stats.school.answer_solve_percentages[index].answer_percentage *
                  100;

              return {
                x: asp.question_number,
                // if base bar is smallest, show it here
                y:
                  (!compareSeries || baseSP < seriesSP) &&
                  (!compareSchool || baseSP < schoolSP)
                    ? baseSP
                    : 0,
                asp,
                index,
              };
            })}
          />

          {compareSeries && compareSchool && (
            <VerticalBarSeries
              cluster="answer"
              onValueMouseOut={() => this.setState({ answerSolveHint: null })}
              onValueMouseOver={d => this.setState({ answerSolveHint: d })}
              barWidth={0.5}
              color={answerSeriesColor}
              data={stats[base].answer_solve_percentages.map((asp, index) => {
                const baseSP = asp.answer_percentage * 100;
                const seriesSP =
                  stats.series.answer_solve_percentages[index]
                    .answer_percentage * 100;
                const schoolSP =
                  stats.school.answer_solve_percentages[index]
                    .answer_percentage * 100;

                let y = 0;
                if (seriesSP < baseSP && seriesSP > schoolSP) {
                  y = seriesSP - schoolSP;
                } else if (seriesSP < schoolSP && seriesSP > baseSP) {
                  y = seriesSP - baseSP;
                }

                return {
                  x: asp.question_number,
                  // if series bar is second smallest, show it here
                  y,
                  asp,
                  index,
                };
              })}
            />
          )}

          {compareSeries && compareSchool && (
            <VerticalBarSeries
              cluster="answer"
              onValueMouseOut={() => this.setState({ answerSolveHint: null })}
              onValueMouseOver={d => this.setState({ answerSolveHint: d })}
              barWidth={0.5}
              color={answerSchoolColor}
              data={stats[base].answer_solve_percentages.map((asp, index) => {
                const baseSP = asp.answer_percentage * 100;
                const seriesSP =
                  stats.series.answer_solve_percentages[index]
                    .answer_percentage * 100;
                const schoolSP =
                  stats.school.answer_solve_percentages[index]
                    .answer_percentage * 100;

                let y = 0;
                if (schoolSP < baseSP && schoolSP > seriesSP) {
                  y = schoolSP - seriesSP;
                } else if (schoolSP < seriesSP && schoolSP > baseSP) {
                  y = schoolSP - baseSP;
                }

                return {
                  x: asp.question_number,
                  // if school bar is second smallest, show it here
                  y,
                  asp,
                  index,
                };
              })}
            />
          )}

          {compareSeries && compareSchool && (
            <VerticalBarSeries
              cluster="answer"
              onValueMouseOut={() => this.setState({ answerSolveHint: null })}
              onValueMouseOver={d => this.setState({ answerSolveHint: d })}
              barWidth={0.5}
              color={answerBaseColor}
              data={stats[base].answer_solve_percentages.map((asp, index) => {
                const baseSP = asp.answer_percentage * 100;
                const seriesSP =
                  stats.series.answer_solve_percentages[index]
                    .answer_percentage * 100;
                const schoolSP =
                  stats.school.answer_solve_percentages[index]
                    .answer_percentage * 100;

                let y = 0;
                if (baseSP < seriesSP && baseSP > schoolSP) {
                  y = baseSP - schoolSP;
                } else if (baseSP < schoolSP && baseSP > seriesSP) {
                  y = baseSP - seriesSP;
                }

                return {
                  x: asp.question_number,
                  // if base bar is second smallest, show it here
                  y,
                  asp,
                  index,
                };
              })}
            />
          )}

          {compareSeries && (
            <VerticalBarSeries
              cluster="answer"
              onValueMouseOut={() => this.setState({ answerSolveHint: null })}
              onValueMouseOver={d => this.setState({ answerSolveHint: d })}
              barWidth={0.5}
              color={answerSeriesColor}
              data={stats[base].answer_solve_percentages.map((asp, index) => {
                const baseSP = asp.answer_percentage * 100;
                const seriesSP =
                  stats.series.answer_solve_percentages[index]
                    .answer_percentage * 100;
                const schoolSP =
                  stats.school &&
                  stats.school.answer_solve_percentages[index]
                    .answer_percentage * 100;

                let y = 0;
                if (
                  seriesSP > baseSP &&
                  (!compareSchool || seriesSP > schoolSP)
                ) {
                  if (schoolSP && schoolSP > baseSP) y = seriesSP - schoolSP;
                  else y = seriesSP - baseSP;
                }

                return {
                  x: asp.question_number,
                  // if series bar is biggest, show it here
                  y,
                  asp,
                  index,
                };
              })}
            />
          )}

          {compareSchool && (
            <VerticalBarSeries
              cluster="answer"
              onValueMouseOut={() => this.setState({ answerSolveHint: null })}
              onValueMouseOver={d => this.setState({ answerSolveHint: d })}
              barWidth={0.5}
              color={answerSchoolColor}
              data={stats[base].answer_solve_percentages.map((asp, index) => {
                const baseSP = asp.answer_percentage * 100;
                const seriesSP =
                  stats.series &&
                  stats.series.answer_solve_percentages[index]
                    .answer_percentage * 100;
                const schoolSP =
                  stats.school.answer_solve_percentages[index]
                    .answer_percentage * 100;

                let y = 0;
                if (
                  schoolSP > baseSP &&
                  (!compareSeries || schoolSP > seriesSP)
                ) {
                  if (seriesSP && seriesSP > baseSP) y = schoolSP - seriesSP;
                  else y = schoolSP - baseSP;
                }

                return {
                  x: asp.question_number,
                  // if school bar is biggest, show it here
                  y,
                  asp,
                  index,
                };
              })}
            />
          )}

          <VerticalBarSeries
            cluster="answer"
            onValueMouseOut={() => this.setState({ answerSolveHint: null })}
            onValueMouseOver={d => this.setState({ answerSolveHint: d })}
            barWidth={0.5}
            color={answerBaseColor}
            data={stats[base].answer_solve_percentages.map((asp, index) => {
              const baseSP = asp.answer_percentage * 100;
              const seriesSP =
                stats.series &&
                stats.series.answer_solve_percentages[index].answer_percentage *
                  100;
              const schoolSP =
                stats.school &&
                stats.school.answer_solve_percentages[index].answer_percentage *
                  100;

              let y = 0;
              if (
                compareSeries &&
                compareSchool &&
                baseSP > seriesSP &&
                baseSP > schoolSP
              ) {
                if (seriesSP > schoolSP) y = baseSP - seriesSP;
                else y = baseSP - schoolSP;
              } else if (compareSeries && !compareSchool && baseSP > seriesSP) {
                y = baseSP - seriesSP;
              } else if (!compareSeries && compareSchool && baseSP > schoolSP) {
                y = baseSP - schoolSP;
              }

              return {
                x: asp.question_number,
                // if base bar is biggest, show it here
                y,
                asp,
                index,
              };
            })}
          />

          <XAxis />
          <YAxis tickFormat={v => `${v}%`} />

          {answerSolveHint && (
            <Hint
              value={answerSolveHint}
              style={{ marginBottom: '10px' }}
              format={d => {
                const baseASP = stats[base].answer_solve_percentages[d.index];
                const seriesASP =
                  stats.series &&
                  stats.series.answer_solve_percentages[d.index];
                const schoolASP =
                  stats.school &&
                  stats.school.answer_solve_percentages[d.index];

                const lines = [];
                lines.push({
                  title: (
                    <FormattedMessage
                      id="stats-solve-percentage"
                      defaultMessage="ratkaisuprosentti"
                    />
                  ),
                  value: `${Math.round(baseASP.solve_percentage * 100)}%`,
                });

                if (compareSeries) {
                  lines.push({
                    title: (
                      <span>
                        <i className="fa fa-globe text-white mr-2" />
                        <FormattedMessage
                          id="stats-solve-percentage"
                          defaultMessage="ratkaisuprosentti"
                        />
                      </span>
                    ),
                    value: `${Math.round(seriesASP.solve_percentage * 100)}%`,
                  });
                }

                if (compareSchool) {
                  lines.push({
                    title: (
                      <span>
                        <i className="fa fa-school text-white mr-2" />
                        <FormattedMessage
                          id="stats-solve-percentage"
                          defaultMessage="ratkaisuprosentti"
                        />
                      </span>
                    ),
                    value: `${Math.round(schoolASP.solve_percentage * 100)}%`,
                  });
                }

                lines.push({
                  title: (
                    <FormattedMessage
                      id="stats-answer-percentage"
                      defaultMessage="vastausprosentti"
                    />
                  ),
                  value: `${Math.round(baseASP.answer_percentage * 100)}%`,
                });

                if (compareSeries) {
                  lines.push({
                    title: (
                      <span>
                        <i className="fa fa-globe text-white mr-2" />
                        <FormattedMessage
                          id="stats-answer-percentage"
                          defaultMessage="vastausprosentti"
                        />
                      </span>
                    ),
                    value: `${Math.round(seriesASP.answer_percentage * 100)}%`,
                  });
                }

                if (compareSchool) {
                  lines.push({
                    title: (
                      <span>
                        <i className="fa fa-school text-white mr-2" />
                        <FormattedMessage
                          id="stats-answer-percentage"
                          defaultMessage="vastausprosentti"
                        />
                      </span>
                    ),
                    value: `${Math.round(schoolASP.answer_percentage * 100)}%`,
                  });
                }

                return lines;
              }}
              align={{ vertical: 'top', horizontal: 'right' }}
            />
          )}
        </FlexibleWidthXYPlot>

        <DiscreteColorLegend
          items={legend}
          orientation="horizontal"
          style={{ textAlign: 'center' }}
        />
      </>
    );
  };

  render() {
    const {
      stats,
      base,
      compareSeries,
      compareSchool,
      showParticipation,
    } = this.props;

    const hasNecessaryStats =
      stats[base] &&
      (!compareSeries || stats.series) &&
      (!compareSchool || stats.school);

    if (!hasNecessaryStats) return <Spinner />;

    return (
      <>
        {showParticipation && this.renderParticipationStats(stats[base])}
        {this.renderPointsStats(stats[base])}
        {this.renderStreakStats(stats[base])}
        {this.renderPointsDistribution(stats[base])}
        {this.renderAnswerDistribution(stats[base])}
        {this.renderSolvePercentages(stats[base])}
      </>
    );
  }
}

export default Stats;
